// src/services/authService.js
import axios from "axios";
import API_BASE_URL from "../config/apiConfig";
const API_URL = `${API_BASE_URL}/auth`;

/* --- Cookie Utilities (An toàn hơn localStorage) --- */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days = 7, options = {}) => {
  let cookieString = `${name}=${value}`;
  
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    cookieString += `; expires=${date.toUTCString()}`;
  }
  
  // Thiết lập các options bảo mật
  const isHttps = typeof window !== 'undefined' && window.location?.protocol === 'https:';
  const defaultOptions = {
    path: '/',
    secure: isHttps, // chỉ bật secure khi đang chạy HTTPS
    sameSite: 'lax', // an toàn hơn cho SPA, vẫn chặn cross-site hầu hết trường hợp
    ...options
  };
  
  Object.entries(defaultOptions).forEach(([key, value]) => {
    if (value === true) {
      cookieString += `; ${key}`;
    } else if (value !== false && value !== null && value !== undefined) {
      cookieString += `; ${key}=${value}`;
    }
  });
  
  document.cookie = cookieString;
};

const deleteCookie = (name) => {
  setCookie(name, '', -1);
};

/* --- Helpers lưu trữ AN TOÀN --- */
const setTokens = (accessToken, refreshToken, user) => {
  // Lưu tokens vào cookies với bảo mật cao
  setCookie("accessToken", accessToken, 1, { 
    secure: typeof window !== 'undefined' && window.location?.protocol === 'https:',
    sameSite: 'lax'
  }); // 1 ngày
  
  setCookie("refreshToken", refreshToken, 7, { 
    secure: typeof window !== 'undefined' && window.location?.protocol === 'https:',
    sameSite: 'lax'
  }); // 7 ngày
  
  // User info (không sensitive) có thể lưu localStorage
  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  }
};

const getAccessToken = () => getCookie("accessToken");
const getRefreshToken = () => getCookie("refreshToken");

const getCurrentUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

const clearTokens = () => {
  // Xóa cookies
  deleteCookie("accessToken");
  deleteCookie("refreshToken");
  
  // Xóa user info
  localStorage.removeItem("user");
};

const buildAuthHeaders = () => {
  const token = getAccessToken();
  if (!token) {
    throw new Error("Không tìm thấy access token. Vui lòng đăng nhập lại.");
  }
  return {
    Authorization: `Bearer ${token}`
  };
};

/* --- API calls --- */
const login = async (formData) => {
  try {
    // Đảm bảo gửi đúng format mà backend mong đợi
    const loginData = {
      email: formData.email,
      password: formData.password
    };
    
    const res = await axios.post(`${API_URL}/login`, loginData);
    const responseData = res.data?.data;

    // Kiểm tra nếu cần Face ID verification - KHÔNG lưu token
    if (responseData?.requiresFaceId) {
      return {
        requiresFaceId: true,
        faceIdToken: responseData.faceIdToken,
        challengeExpiresIn: responseData.challengeExpiresIn,
        hasTwoFactorBackup: responseData.hasTwoFactorBackup || false,
        user: responseData.user
      };
    }

    // Kiểm tra nếu cần 2FA verification - KHÔNG lưu token
    if (responseData?.requiresTwoFactor) {
      return {
        requiresTwoFactor: true,
        twoFactorToken: responseData.twoFactorToken,
        challengeExpiresIn: responseData.challengeExpiresIn,
        user: responseData.user
      };
    }

    // Chỉ lưu token khi đã verify xong (không có requiresFaceId hoặc requiresTwoFactor)
    const { token, refresh, user, sessionId } = responseData;
    if (!token) {
      throw new Error("Không nhận được token từ server");
    }

    if (user && sessionId && !user.sessionId) {
      user.sessionId = sessionId;
    }

    // ✅ lưu token và user
    setTokens(token, refresh, user);

    return { accessToken: token, refreshToken: refresh, sessionId, user };
  } catch (err) {
    // Xử lý error từ response của server
    if (err.response?.data) {
      // Ném lại error với thông tin chi tiết từ backend
      const error = new Error(err.response.data.message || 'Login failed');
      error.response = err.response;
      throw error;
    }
    throw new Error("Login failed");
  }
};

const verifyTwoFactorLogin = async ({ twoFactorToken, otp, backupCode }) => {
  try {
    const requestPayload = {
      twoFactorToken
    };

    if (otp) {
      requestPayload.otp = otp;
    }

    if (backupCode) {
      requestPayload.backupCode = backupCode;
    }

    const res = await axios.post(`${API_URL}/2fa/login`, requestPayload);
    const data = res.data.data;
    
    // Nếu có Face ID requirement, trả về ngay mà không lưu token
    if (data.requiresFaceId) {
      return data;
    }

    const { token, refresh, user, sessionId } = data;
    if (user && sessionId && !user.sessionId) {
      user.sessionId = sessionId;
    }

    setTokens(token, refresh, user);
    return { accessToken: token, refreshToken: refresh, sessionId, user };
  } catch (err) {
    if (err.response?.data) {
      const error = new Error(err.response.data.message || 'Two-factor verification failed');
      error.response = err.response;
      throw error;
    }
    throw new Error("Two-factor verification failed");
  }
};

const logout = async () => {
  try {
    const refreshToken = getRefreshToken();
    const accessToken = getAccessToken();

    await axios.post(
      `${API_URL}/logout`,
      { refreshToken },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    clearTokens();
    return { message: "Logout successful" };
  } catch (err) {
    clearTokens();
    throw err.response?.data || { message: "Logout failed" };
  }
};

const refreshTokenFn = async () => {
  try {
    const refreshToken = getRefreshToken();

    const res = await axios.post(
      `${API_URL}/refresh`,
      { refreshToken }
    );

    const { token: newAccessToken, sessionId } = res.data.data;

    // ⚠️ giữ nguyên user cũ khi refresh
    const user = getCurrentUser();
    if (user && sessionId) {
      user.sessionId = sessionId;
      localStorage.setItem("user", JSON.stringify(user));
    }
    setTokens(newAccessToken, refreshToken, user);

    return newAccessToken;
  } catch (err) {
    clearTokens();
    throw err.response?.data || { message: "Refresh token failed" };
  }
};

const fetchTwoFactorStatus = async () => {
  try {
    const res = await axios.get(`${API_URL}/2fa/status`, {
      headers: buildAuthHeaders()
    });
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể lấy trạng thái 2FA" };
  }
};

const initiateTwoFactorSetup = async () => {
  try {
    const res = await axios.post(
      `${API_URL}/2fa/setup`,
      {},
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể khởi tạo 2FA" };
  }
};

const confirmTwoFactorSetup = async (token) => {
  try {
    const res = await axios.post(
      `${API_URL}/2fa/verify`,
      { token },
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Xác minh 2FA thất bại" };
  }
};

const disableTwoFactor = async ({ token, backupCode }) => {
  try {
    const payload = {};
    if (token) payload.token = token;
    if (backupCode) payload.backupCode = backupCode;

    const res = await axios.post(
      `${API_URL}/2fa/disable`,
      payload,
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể tắt 2FA" };
  }
};

const listSessions = async ({ page = 1, pageSize = 10 } = {}) => {
  try {
    const res = await axios.get(`${API_URL}/sessions`, {
      params: { page, pageSize },
      headers: buildAuthHeaders()
    });
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể tải danh sách phiên" };
  }
};

const revokeSession = async (sessionId) => {
  try {
    const res = await axios.post(
      `${API_URL}/sessions/revoke`,
      { sessionId },
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể đăng xuất phiên đã chọn" };
  }
};

const revokeOtherSessions = async (includeCurrent = false) => {
  try {
    const payload = includeCurrent ? { includeCurrent: true } : {};
    const res = await axios.post(
      `${API_URL}/sessions/revoke-others`,
      payload,
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể đăng xuất các phiên khác" };
  }
};

const clearRevokedSessions = async () => {
  try {
    const res = await axios.delete(
      `${API_URL}/sessions/revoked`,
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể xoá các phiên đã đăng xuất" };
  }
};

// Face ID methods
const getFaceIdStatus = async () => {
  try {
    const res = await axios.get(`${API_URL}/faceid/status`, {
      headers: buildAuthHeaders()
    });
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể lấy trạng thái Face ID" };
  }
};

const listFaceIds = async () => {
  try {
    const res = await axios.get(`${API_URL}/faceid/list`, {
      headers: buildAuthHeaders()
    });
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể lấy danh sách Face ID" };
  }
};

const registerFaceId = async (faceEmbedding, imageFile) => {
  try {
    const formData = new FormData();
    formData.append('faceEmbedding', JSON.stringify(faceEmbedding));
    if (imageFile) {
      formData.append('image', imageFile);
    }

    const res = await axios.post(
      `${API_URL}/faceid/register`,
      formData,
      {
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể đăng ký Face ID" };
  }
};

const deleteFaceId = async (faceIdId) => {
  try {
    const res = await axios.delete(
      `${API_URL}/faceid/${faceIdId}`,
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Không thể xóa Face ID" };
  }
};

const verifyFaceId = async (faceEmbedding, userId) => {
  try {
    const res = await axios.post(
      `${API_URL}/faceid/verify`,
      { faceEmbedding: JSON.stringify(faceEmbedding), userId },
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Xác thực Face ID thất bại" };
  }
};

const verifyFaceIdBackupCode = async (backupCode, userId) => {
  try {
    const res = await axios.post(
      `${API_URL}/faceid/verify-backup`,
      { backupCode, userId },
      { headers: buildAuthHeaders() }
    );
    return res.data.data;
  } catch (err) {
    throw err.response?.data || { message: "Xác thực mã bảo mật thất bại" };
  }
};

const verifyFaceIdLogin = async (faceIdToken, faceEmbedding, backupCode) => {
  try {

    const payload = { faceIdToken };
    if (faceEmbedding) {
      payload.faceEmbedding = JSON.stringify(faceEmbedding);
    }
    if (backupCode) {
      payload.backupCode = backupCode;
    }

    
    // Đây là public API, không cần authentication headers
    const res = await axios.post(
      `${API_URL}/faceid/login`,
      payload
      // Không cần headers vì đây là public API (chưa authenticated)
    );
    
    
    const data = res.data.data;
    
    // Nếu có yêu cầu 2FA (fallback từ Face ID fail), trả về ngay không lưu token
    if (data.requiresTwoFactor) {
      return data;
    }
    
    // Save tokens and user
    if (data.token && data.user) {
      setTokens(data.token, data.refresh, data.user);
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
    }
    
    return data;
  } catch (err) {
    console.error('[verifyFaceIdLogin] Error:', err);
    console.error('[verifyFaceIdLogin] Error response:', err.response?.data);
    
    // Kiểm tra nếu có requiresTwoFactor trong error response
    const errorData = err.response?.data?.data;
    if (errorData && errorData.requiresTwoFactor) {
      return errorData;
    }
    throw err.response?.data || { message: "Xác thực Face ID thất bại" };
  }
};

const switchFaceIdToTwoFactor = async (faceIdToken) => {
  try {
    const res = await axios.post(
      `${API_URL}/faceid/switch-to-2fa`,
      { faceIdToken }
      // Không cần headers vì đây là API public (chưa authenticated)
    );
    return res.data.data;
  } catch (err) {
    console.error('Switch to 2FA API error:', err.response?.data || err.message);
    const errorResponse = err.response?.data;
    if (errorResponse?.message) {
      throw { message: errorResponse.message, errorMessage: errorResponse.message };
    }
    throw { message: "Không thể chuyển sang 2FA. Vui lòng thử lại.", errorMessage: "Không thể chuyển sang 2FA" };
  }
};

/* --- Export object --- */
const authService = {
  login,
  verifyTwoFactorLogin,
  logout,
  refreshTokenFn,
  getAccessToken,
  getRefreshToken,
  getCurrentUser,
  // Thêm các method bảo mật
  isAuthenticated: () => {
    const token = getAccessToken();
    const user = getCurrentUser();
    return !!(token && user);
  },
  
  // Kiểm tra role admin
  isAdmin: () => {
    const user = getCurrentUser();
    if (!user) return false;
    // Kiểm tra roles array
    if (user.roles && Array.isArray(user.roles)) {
      const hasAdminRole = user.roles.some(role => 
        role === 'Admin' || role === 'SuperAdmin' || 
        role === 'admin' || role === 'superAdmin'
      );
      return hasAdminRole;
    }
    
    // Fallback cho role string (nếu có)
    const hasStringRole = user.role === 'admin' || user.role === 'superAdmin';
    return hasStringRole;
  },
  
  // Kiểm tra role superAdmin
  isSuperAdmin: () => {
    const user = getCurrentUser();
    if (!user) return false;
    
    // Kiểm tra roles array
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.some(role => 
        role === 'SuperAdmin' || role === 'superAdmin'
      );
    }
    
    // Fallback cho role string (nếu có)
    return user.role === 'superAdmin';
  },
  
  // Kiểm tra quyền truy cập admin
  hasAdminAccess: () => {
    return authService.isAuthenticated() && authService.isAdmin();
  },
  clearTokens,
  // Method để setup axios interceptors (sẽ dùng trong App.js)
  setupInterceptors: (() => {
    let initialized = false;

    return () => {
      if (initialized) {
        return;
      }
      initialized = true;

      // Request interceptor - tự động thêm token
      axios.interceptors.request.use(
        (config) => {
          const token = getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      // Response interceptor - handle token expiry
      axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error.config;
          const status = error.response?.status;
          const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');

          if (status === 401 && isRefreshRequest) {
            clearTokens();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          if (status === 401 && originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
              const newToken = await refreshTokenFn();

              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${newToken}`
              };
              return axios(originalRequest);
            } catch (refreshError) {
              clearTokens();
              window.location.href = '/login';
              return Promise.reject(refreshError);
            }
          }

          return Promise.reject(error);
        }
      );
    };
  })(),
  twoFactor: {
    getStatus: fetchTwoFactorStatus,
    initiateSetup: initiateTwoFactorSetup,
    confirmSetup: confirmTwoFactorSetup,
    disable: disableTwoFactor
  },
  sessions: {
    list: listSessions,
    revoke: revokeSession,
    revokeOthers: revokeOtherSessions,
    clearRevoked: clearRevokedSessions
  },
  faceId: {
    getStatus: getFaceIdStatus,
    list: listFaceIds,
    register: registerFaceId,
    delete: deleteFaceId,
    verify: verifyFaceId,
    verifyBackupCode: verifyFaceIdBackupCode,
    verifyLogin: verifyFaceIdLogin,
    switchTo2FA: switchFaceIdToTwoFactor
  }
};

export default authService;
